import { i18n } from '../i18n.js';
/**
 * UI Indicators Module
 * Handles health, mana, and deck indicators
 */

import { AccentContrast } from './accent-contrast.js';
import { ActionIntent } from './action-intent.js';
import { CardbackArt } from './cardback-art.js';
import { CardBackRaster } from './card-back-raster.js';
import { CardDrag } from './card-drag.js';
import { Cards } from './cards.js';
import { Effects } from './effects.js';
import { FX } from './fx.js';
import { splitStatusSegments } from './status-segments.js';
import { StoryFeed } from './story-feed.js';
import { TouchPreview } from './touch-preview.js';
import { GameState } from '../game-state.js';
import { SocketHandler } from '../socket-handler.js';
import { uiPx } from './ui-scale.js';
let Indicators = {};

// Create UI namespace if it doesn't exist

// Indicators module
Indicators = (function () {
    'use strict';

    /* The status chip's own background — rgba(14, 9, 22, 0.94) over a dark
       portrait — as an opaque hex, so contrast can be measured against the
       surface the text is actually drawn on. Kept in step with
       `.status-effect-chip` in status-indicator.css. */
    const CHIP_INK = '#0f0a17';

    // Track created elements to avoid duplicates
    let playerDeckCreated = false;
    let opponentDeckCreated = false;
    const portraitRuntimeKey = Symbol.for('moonfall.indicators.portraitRuntime');
    window[portraitRuntimeKey]?.dispose?.();
    const PORTRAIT_DOWNSAMPLE_CACHE_LIMIT = 24;
    const PORTRAIT_MIN_SAMPLE_WIDTH = 96;
    const PORTRAIT_IMAGE_LOAD_TIMEOUT_MS = 3000;
    const PORTRAIT_RETRY_DELAY_MS = 5000;
    const SUMMONER_NAME_MIN_FONT_PX = 9;
    const PORTRAIT_DIRECT_SAMPLE = Symbol('portrait-direct-sample');
    const portraitDownsampleCache = new Map();
    /* blob: URLs of the samples this module made (encodePortraitSample), with
       the decoded image that keeps each one's pixels in the memory cache. */
    const portraitSampleImages = new Map();
    const portraitBackgroundPreloadCache = new Map();
    const portraitRenderTasks = new WeakMap();
    const portraitResizeObservers = new Map();
    let portraitDensityQuery = null;
    const portraitSourceLoaders = new Map();
    let portraitRenderRequestId = 0;
    const summonerNameplates = new Set();
    let summonerNameplateFitFrame = 0;
    let summonerNameplateResizeObserver = null;
    const MANA_FINE_HOVER_CLASS = 'mana-indicator--fine-hovered';
    const fineHoverMedia = typeof window.matchMedia === 'function'
        ? window.matchMedia('(any-hover: hover) and (any-pointer: fine)')
        : null;
    const manaPressBindings = new WeakSet();
    const pressedManaByPointer = new Map();
    let hasSuppressedManaHover = false;

    function clearManaFineHover(mana = null) {
        if (mana) {
            mana.classList.remove(MANA_FINE_HOVER_CLASS);
            return;
        }
        document.querySelectorAll(`.${MANA_FINE_HOVER_CLASS}`)
            .forEach((indicator) => indicator.classList.remove(MANA_FINE_HOVER_CLASS));
    }

    function suppressCompatibilityHover(mana) {
        clearManaFineHover(mana);
        mana.classList.add('mana-indicator--touch-hover-suppressed');
        mana.closest?.('.player-portrait')?.classList.add('portrait--touch-hover-suppressed');
        hasSuppressedManaHover = true;
    }

    function clearCompatibilityHover(event) {
        if (event && event.pointerType !== 'mouse') return;
        // WebKit may synthesize mouse pointermoves both while a finger is down
        // and during TouchPreview's post-touch compatibility-event window.
        // Neither is evidence that the player switched input modality. A mouse
        // pointerdown remains an immediate, unambiguous handoff.
        if (
            event?.type === 'pointermove' &&
            TouchPreview?.isSuppressingMouse?.()
        ) return;
        if (pressedManaByPointer.size > 0) return;
        if (!hasSuppressedManaHover) return;
        document.querySelectorAll('.mana-indicator--touch-hover-suppressed')
            .forEach((mana) => mana.classList.remove('mana-indicator--touch-hover-suppressed'));
        document.querySelectorAll('.portrait--touch-hover-suppressed')
            .forEach((portrait) => portrait.classList.remove('portrait--touch-hover-suppressed'));
        hasSuppressedManaHover = false;
    }

    function releaseManaPress(event) {
        const mana = pressedManaByPointer.get(event.pointerId);
        if (!mana) return;
        mana.classList.remove('mana-indicator--pressed');
        pressedManaByPointer.delete(event.pointerId);
    }

    function clearManaPresses() {
        pressedManaByPointer.forEach((mana) => {
            mana.classList.remove('mana-indicator--pressed');
        });
        pressedManaByPointer.clear();
    }

    function clearManaInteractionState() {
        clearManaPresses();
        clearManaFineHover();
    }

    function handleManaFinePointerMove(event) {
        if (event.pointerType !== 'mouse') return;
        if (fineHoverMedia && !fineHoverMedia.matches) return;
        if (pressedManaByPointer.size > 0) return;
        if (TouchPreview?.isSuppressingMouse?.()) return;
        const mana = event.currentTarget;
        if (mana.classList.contains('mana-indicator--touch-hover-suppressed')) return;
        mana.classList.add(MANA_FINE_HOVER_CLASS);
    }

    document.addEventListener('pointerup', releaseManaPress, true);
    document.addEventListener('pointercancel', releaseManaPress, true);
    // A hybrid touch laptop can synthesize sticky :hover after a finger tap.
    // Keep that compatibility hover neutral until the user actually moves a
    // mouse again; that real fine-pointer motion restores ordinary hover.
    document.addEventListener('pointermove', clearCompatibilityHover, true);
    document.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') clearCompatibilityHover(event);
    }, true);
    window.addEventListener('blur', clearManaInteractionState);
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) clearManaInteractionState();
    });
    fineHoverMedia?.addEventListener?.('change', clearManaInteractionState);

    function bindManaPressFeedback(mana) {
        if (!mana || manaPressBindings.has(mana)) return;
        manaPressBindings.add(mana);
        mana.addEventListener('pointerdown', (event) => {
            if (event.pointerType === 'mouse' || event.isPrimary === false) return;
            pressedManaByPointer.forEach((pressed, pointerId) => {
                if (pressed !== mana) return;
                pressed.classList.remove('mana-indicator--pressed');
                pressedManaByPointer.delete(pointerId);
            });
            pressedManaByPointer.set(event.pointerId, mana);
            suppressCompatibilityHover(mana);
            mana.classList.add('mana-indicator--pressed');
        });
        mana.addEventListener('pointermove', handleManaFinePointerMove);
        mana.addEventListener('pointerleave', (event) => {
            releaseManaPress(event);
            clearManaFineHover(mana);
        });
        mana.addEventListener('lostpointercapture', releaseManaPress);
    }

    function clearStatusIndicators() {
        clearManaInteractionState();
        clearAllPortraitSourceLoaders();
        ['player-deck', 'opponent-deck'].forEach((id) => {
            CardbackArt?.clear?.(document.getElementById(id));
        });
        portraitResizeObservers.forEach((observer) => observer.disconnect());
        portraitResizeObservers.clear();
        document.querySelectorAll('.player-portrait').forEach((portrait) => {
            portrait.dataset.portraitRenderRequest = `retired-${++portraitRenderRequestId}`;
        });
        document.querySelectorAll('.mana-indicator--touch-hover-suppressed')
            .forEach((mana) => mana.classList.remove('mana-indicator--touch-hover-suppressed'));
        document.querySelectorAll('.portrait--touch-hover-suppressed')
            .forEach((portrait) => portrait.classList.remove('portrait--touch-hover-suppressed'));
        hasSuppressedManaHover = false;
        [
            'player-status-indicator',
            'opponent-status-indicator',
        ].forEach((id) => document.getElementById(id)?.remove());
        document.querySelectorAll('.portrait--has-status-effects')
            .forEach((portrait) => portrait.classList.remove('portrait--has-status-effects'));
        document.dispatchEvent(new Event('moonfall:status-layout-changed'));
    }

    function statusEffects(status) {
        /* SEGMENTATION IS SHARED — status-segments.js owns the separator rule
           and the no-glyph drop, so the card plate and the status-change
           reaction cut the identical string identically. It used to live here
           and only here, which is exactly how cards.js kept splitting on `;`
           alone and printing a raw `|` as its first glyph.
           The `**` strip stays local: this surface strips the markers anywhere
           in an effect, the card plate only when they wrap the whole token.
           Neither changes what the text SAYS. */
        return splitStatusSegments(status)
            .map((raw) => raw.replace(/\*\*/g, '').trim())
            .filter(Boolean);
    }

    function statusAriaLabel(key, fallback, effects) {
        const status = effects.join('; ');
        const translated = i18n?.t?.(key, { status });
        return typeof translated === 'string' && translated !== key
            ? translated
            : fallback.replace('{{status}}', status);
    }

    function renderStatusIndicator({ id, portrait, status, labelKey, labelFallback }) {
        let indicator = document.getElementById(id);
        const effects = statusEffects(status);

        if (!effects.length) {
            portrait?.classList.remove('portrait--has-status-effects');
            if (indicator && indicator.style.display !== 'none') {
                indicator.style.display = 'none';
                indicator.removeAttribute('aria-label');
                document.dispatchEvent(new Event('moonfall:status-layout-changed'));
            }
            return;
        }

        if (!indicator) {
            if (!portrait) return;
            indicator = document.createElement('div');
            indicator.id = id;
            indicator.className = 'status-indicator';
            indicator.setAttribute('role', 'status');
            indicator.setAttribute('aria-live', 'polite');
            indicator.setAttribute('aria-atomic', 'true');
            portrait.appendChild(indicator);
        }
        portrait?.classList.add('portrait--has-status-effects');

        const signature = JSON.stringify(effects);
        const changed = indicator.dataset.statusSignature !== signature;
        const wasHidden = indicator.style.display === 'none';
        const ariaLabel = statusAriaLabel(labelKey, labelFallback, effects);
        if (changed || wasHidden || indicator.getAttribute('aria-label') !== ariaLabel) {
            indicator.setAttribute('aria-label', ariaLabel);
        }
        if (changed) {
            const fragment = document.createDocumentFragment();
            effects.forEach((effect) => {
                const chip = document.createElement('span');
                chip.className = 'status-effect-chip';
                chip.textContent = effect;
                // One word, one colour: the same helper the card overlay and the
                // storyboard prose use, so a status is recognisable by its
                // colour wherever it appears. The word carries it — tinting only
                // the chip's edge left every status the same cream text.
                const accent = StoryFeed?.getStatusColor?.(effect);
                if (accent) {
                    chip.style.setProperty('--status-effect-accent', accent);
                    /* The edge keeps the palette colour exactly; the WORD gets
                       the same hue at a lightness that clears WCAG AA on the
                       chip's near-black ribbon. Two of the eight palette
                       entries measure ~4.1:1 there, and the palette is handed
                       out in registration order, so a real match reaches them.
                       The six that already pass are returned unchanged. */
                    const readable = AccentContrast?.ensure?.(accent, CHIP_INK, 4.5);
                    if (readable) chip.style.setProperty('--status-effect-text', readable);
                }
                fragment.appendChild(chip);
            });
            indicator.replaceChildren(fragment);
            indicator.dataset.statusSignature = signature;
            indicator.dataset.effectCount = String(effects.length);
        }

        indicator.style.display = 'flex';
        // CSS owns the layout at each viewport — one effect per row
        // (status-indicator.css) — so no inline direction may override it.
        indicator.style.removeProperty('flex-direction');
        indicator.style.removeProperty('align-items');
        if (changed || wasHidden) {
            document.dispatchEvent(new Event('moonfall:status-layout-changed'));
        }
    }

    function routeActionDrop(dropData, targetType, targetId, targetIndex) {
        if (ActionIntent && typeof ActionIntent.routeOrPlay === 'function') {
            return ActionIntent.routeOrPlay(dropData, targetType, targetId, targetIndex);
        }
        if (Number.isInteger(targetIndex)) {
            return SocketHandler.playAction(dropData.instanceId, targetType, targetId, targetIndex);
        }
        return SocketHandler.playAction(dropData.instanceId, targetType, targetId);
    }

    function isMoonFuseInterimPlaceholderSource(value) {
        if (typeof value !== 'string' || !value.trim()) return false;
        try {
            let pathname = new URL(
                value.trim(),
                window.location?.href || 'http://localhost/',
            ).pathname;
            for (let count = 0; count < 4; count += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            return pathname === '/assets/art_placeholder.webp';
        } catch (_) {
            return false;
        }
    }

    function sanitizeDirectMediaUrl(value) {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        if (
            !trimmed
            || isMoonFuseInterimPlaceholderSource(trimmed)
            || trimmed.startsWith('/api/card-image/')
        ) return null;
        if (
            trimmed.startsWith('http') ||
            trimmed.startsWith('data:') ||
            trimmed.startsWith('blob:') ||
            trimmed.startsWith('/') ||
            trimmed.startsWith('/assets/') ||
            trimmed.startsWith('assets/')
        ) {
            return trimmed;
        }
        return null;
    }

    function resolveProfilePictureRequest(value, preferredDirectUrl) {
        const explicitDirectUrl = sanitizeDirectMediaUrl(preferredDirectUrl);
        if (explicitDirectUrl) {
            return {
                directUrl: explicitDirectUrl,
                assetId: '',
            };
        }

        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized || isMoonFuseInterimPlaceholderSource(normalized)) {
            return { directUrl: null, assetId: '' };
        }
        const directUrl = sanitizeDirectMediaUrl(normalized);
        return {
            directUrl,
            assetId: directUrl || normalized.startsWith('/api/card-image/') ? '' : normalized.replace(/^\/+/, ''),
        };
    }

    function ensurePortraitArt(element) {
        if (
            !element ||
            element.tagName !== 'DIV' ||
            !element.classList.contains('player-portrait')
        ) return element;

        let art = Array.from(element.children).find((child) =>
            child.classList?.contains('portrait-art')
        );
        if (!art) {
            art = document.createElement('span');
            art.className = 'portrait-art';
            art.setAttribute('aria-hidden', 'true');
            element.prepend(art);
        }
        return art;
    }

    function nameplateTextFits(nameplate) {
        return nameplate.scrollWidth <= nameplate.clientWidth + 1
            && nameplate.scrollHeight <= nameplate.clientHeight + 1;
    }

    function fitSummonerNameplate(nameplate) {
        if (!nameplate?.isConnected || nameplate.hidden) return;
        const width = nameplate.clientWidth;
        const height = nameplate.clientHeight;
        if (width <= 0 || height <= 0) return;

        const fitKey = `${nameplate.textContent}\u0000${width}x${height}`;
        if (nameplate.dataset.fitKey === fitKey) return;
        nameplate.dataset.fitKey = fitKey;
        delete nameplate.dataset.fit;

        nameplate.style.removeProperty('font-size');
        const preferredSize = Number.parseFloat(getComputedStyle(nameplate).fontSize);
        if (!Number.isFinite(preferredSize)) return;
        nameplate.style.fontSize = `${preferredSize}px`;
        if (nameplateTextFits(nameplate)) return;

        let low = Math.min(uiPx(SUMMONER_NAME_MIN_FONT_PX), preferredSize);
        let high = preferredSize;
        let fitted = low;
        nameplate.style.fontSize = `${low}px`;
        if (!nameplateTextFits(nameplate)) {
            nameplate.dataset.fit = 'truncated';
            return;
        }
        for (let attempt = 0; attempt < 7; attempt += 1) {
            const candidate = (low + high) / 2;
            nameplate.style.fontSize = `${candidate}px`;
            if (nameplateTextFits(nameplate)) {
                fitted = candidate;
                low = candidate;
            } else {
                high = candidate;
            }
        }
        /* Truncated, never rounded: a size rounded UP past the one that fitted
           can push the last word onto a second line. "Orion Nightwhisper" fitted
           one line at 11.625px at 1920x900 and wrapped, 3px over, at the 11.63px
           it used to be written back as (summoner-name-fit.spec.js). */
        nameplate.style.fontSize = `${Math.floor(fitted * 100) / 100}px`;
    }

    function queueSummonerNameplateFit(force = false) {
        if (force) {
            summonerNameplates.forEach((nameplate) => delete nameplate.dataset.fitKey);
        }
        if (summonerNameplateFitFrame) return;
        const schedule = window.requestAnimationFrame || ((callback) => setTimeout(callback, 0));
        summonerNameplateFitFrame = schedule(() => {
            summonerNameplateFitFrame = 0;
            summonerNameplates.forEach((nameplate) => {
                if (!nameplate.isConnected) {
                    summonerNameplateResizeObserver?.unobserve(nameplate);
                    summonerNameplates.delete(nameplate);
                    return;
                }
                fitSummonerNameplate(nameplate);
            });
        });
    }

    function observeSummonerNameplate(nameplate) {
        if (summonerNameplates.has(nameplate)) return;
        summonerNameplates.add(nameplate);
        if (typeof ResizeObserver === 'function') {
            summonerNameplateResizeObserver ||= new ResizeObserver(() => {
                queueSummonerNameplateFit(true);
            });
            summonerNameplateResizeObserver.observe(nameplate);
        }
    }

    window.addEventListener('resize', () => queueSummonerNameplateFit(true), { passive: true });

    function updateSummonerNameplate(portrait, side, value) {
        if (!portrait) return;

        const id = `${side}-summoner-name`;
        let nameplate = document.getElementById(id);
        if (!nameplate || nameplate.parentElement !== portrait) {
            nameplate?.remove();
            nameplate = document.createElement('div');
            nameplate.id = id;
            nameplate.className = 'summoner-nameplate';
            nameplate.dataset.side = side;
            portrait.appendChild(nameplate);
            observeSummonerNameplate(nameplate);
        }

        // The server has already resolved official/custom names into the
        // player's language. Keep that authority string literal and inert.
        const name = typeof value === 'string' ? value.trim() : '';
        nameplate.textContent = name;
        nameplate.hidden = !name;
        nameplate.title = name;
        delete nameplate.dataset.fitKey;
        if (!name) nameplate.style.removeProperty('font-size');

        if (name) {
            portrait.setAttribute('role', 'group');
            portrait.setAttribute('aria-labelledby', id);
        } else {
            portrait.removeAttribute('role');
            portrait.removeAttribute('aria-labelledby');
        }
        queueSummonerNameplateFit();
    }

    function setPortraitPaint(element, imageUrl) {
        const backgroundImage = `url('${imageUrl}')`;
        const paintElement = ensurePortraitArt(element);
        if (paintElement.style.backgroundImage !== backgroundImage) {
            paintElement.style.backgroundImage = backgroundImage;
        }

        // Keep the long-standing inline style contract for code that reads the
        // portrait wrapper. CSS gives the isolated child sole visual ownership.
        if (paintElement !== element && element.style.backgroundImage !== backgroundImage) {
            element.style.backgroundImage = backgroundImage;
        }
    }

    function clearPortraitPaint(element) {
        if (!element) return;
        const paintElement = ensurePortraitArt(element);
        paintElement?.style?.removeProperty('background-image');
        element.style?.removeProperty('background-image');
    }

    function getPortraitPaint(element) {
        const paintElement = ensurePortraitArt(element);
        return element.tagName === 'IMG' ? element.src : paintElement.style.backgroundImage;
    }

    function setPortraitPaintResult(element, value) {
        if (element.tagName === 'IMG') {
            element.src = value;
        } else {
            setPortraitPaint(element, value);
        }
    }

    function ensurePortraitResizeObserver(element) {
        if (
            !element ||
            element.tagName !== 'DIV' ||
            !element.classList.contains('player-portrait') ||
            typeof window.ResizeObserver !== 'function' ||
            portraitResizeObservers.has(element)
        ) return;

        const observer = new window.ResizeObserver(() => {
            if (!element.isConnected) {
                observer.disconnect();
                portraitResizeObservers.delete(element);
                return;
            }
            const rect = element.getBoundingClientRect();
            if (!(rect.width > 0) || !(rect.height > 0)) return;
            const url = element.dataset.portraitSrc;
            if (url) enhancePortraitImage(element, url);
        });
        portraitResizeObservers.set(element, observer);
        observer.observe(element);
        if (!portraitDensityQuery) watchPortraitDensity();
    }

    /* A PORTRAIT IS SAMPLED FOR THE SCREEN IT IS ON: 2 x devicePixelRatio of
       its box. The resize observer above sees layout boxes only, and a window
       moved to a screen of another density keeps its layout box, so the
       portrait stayed sampled for the old screen — soft — until the next game
       snapshot happened to re-sample it mid-match (measured in Firefox: 397x295
       kept at density 2, re-rendered at 794x589 by a later snapshot). Card art
       re-rasters on the same change (cards.js, armLockedRasterDprMonitor). */
    function watchPortraitDensity() {
        if (typeof window.matchMedia !== 'function') return;
        if (portraitDensityQuery) {
            if (typeof portraitDensityQuery.removeEventListener === 'function') {
                portraitDensityQuery.removeEventListener('change', onPortraitDensityChange);
            } else {
                portraitDensityQuery.removeListener?.(onPortraitDensityChange);
            }
        }
        portraitDensityQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
        if (typeof portraitDensityQuery.addEventListener === 'function') {
            portraitDensityQuery.addEventListener('change', onPortraitDensityChange);
        } else {
            portraitDensityQuery.addListener?.(onPortraitDensityChange);
        }
    }

    function onPortraitDensityChange() {
        watchPortraitDensity();
        portraitResizeObservers.forEach((_observer, element) => {
            if (!element.isConnected) return;
            const rect = element.getBoundingClientRect();
            if (!(rect.width > 0) || !(rect.height > 0)) return;
            const url = element.dataset.portraitSrc;
            if (url) enhancePortraitImage(element, url);
        });
    }

    function setPortraitBackground(element, imageUrl) {
        if (!element) return;
        const url = sanitizeDirectMediaUrl(imageUrl);
        if (!url) {
            clearPortraitPaint(element);
            delete element.dataset.portraitSrc;
            element.dataset.portraitQuality = 'unavailable';
            element.classList.add('portrait-art-unavailable');
            return;
        }
        const sourceChanged = element.dataset.portraitSrc !== url;
        if (sourceChanged) {
            delete element.dataset.portraitFailedSampleKey;
            delete element.dataset.portraitSampleRetryAt;
            delete element.dataset.portraitSampleKey;
            delete element.dataset.portraitRenderedSampleKey;
            delete element.dataset.portraitRenderRequest;
            element.dataset.portraitQuality = 'direct';
        }
        element.classList.remove('portrait-art-unavailable', 'portrait-art-retrying');
        element.dataset.portraitSrc = url;
        ensurePortraitResizeObserver(element);
        /* An empty portrait is painted by its sample, which takes its pixels
           from the shared sampler (buildPortraitDownsample) — the very
           decode the match warm made, or one load shared with it. Painting
           the raw URL first asked the art host for the picture again without
           CORS: a second full download from a host that varies by Origin,
           which then evicted the CORS copy and cost the sample a third
           (measured 25 September 2026, phone, 4G), and it showed the aliased
           full image for a moment before the sample replaced it. The raw URL
           is still painted when the sample fails, and a portrait showing
           another summoner is painted directly as before, so the old face
           never lingers. */
        const empty = !getPortraitPaint(element);
        const sharedSampler = typeof window.MoonfallRasterSampler?.prepareDecodedImage === 'function'
            && typeof window.MoonfallRasterSampler?.getDecodedImage === 'function';
        if ((sourceChanged || empty) && !(empty && sharedSampler)) {
            setPortraitPaint(element, url);
        }
        enhancePortraitImage(element, url);
    }

    function cachePortraitDownsample(key, task) {
        portraitDownsampleCache.set(key, task);
        if (portraitDownsampleCache.size <= PORTRAIT_DOWNSAMPLE_CACHE_LIMIT) return;
        const evictedKey = portraitDownsampleCache.keys().next().value;
        const evicted = portraitDownsampleCache.get(evictedKey);
        portraitDownsampleCache.delete(evictedKey);
        // A sample nothing shows any more gives its pixels back.
        Promise.resolve(evicted).then((url) => {
            if (typeof url === 'string' && !portraitSampleShown(url)) releasePortraitSample(url);
        }, () => {});
    }

    /* THE SAMPLE GOES TO CSS AS ITS OWN PIXELS, NOT AS A PNG (round 15, 28
       September 2026). `toDataURL('image/png')` cost 540-840 ms of main
       thread per match start on the 4x-CPU Chrome phone (two portraits) and
       314-407 ms per reload, and painting the multi-megabyte data: URL another
       170-190 ms. The canvas's RGBA bytes are wrapped as they are in an
       uncompressed bitmap (a BMP header whose channel masks say R, G, B, A in
       memory order, rows top-down) behind a blob: URL: nothing is re-encoded
       or resampled, so the painted portrait is the PNG path's pixel for pixel
       (measured at DPR 3: Chromium identical, Firefox and WebKit within 0.005
       levels on average). It is decoded before it is painted, so no frame
       shows an empty portrait. An engine that does not read the bitmap back
       exactly gets a PNG of the same pixels instead (engineReadsBitmapsBack,
       below). A canvas that cannot be read (tainted) or a sample the engine
       cannot decode is a failed sample: the caller keeps the direct picture,
       as it always did. */
    const BMP_HEADER_BYTES = 124; // 14 file + 108 BITMAPV4HEADER + 2 padding (4-aligned rows)

    function portraitBitmapBlob(pixels, width, height) {
        const header = new ArrayBuffer(BMP_HEADER_BYTES);
        const view = new DataView(header);
        view.setUint8(0, 0x42); // 'B'
        view.setUint8(1, 0x4d); // 'M'
        view.setUint32(2, BMP_HEADER_BYTES + pixels.length, true);
        view.setUint32(10, BMP_HEADER_BYTES, true); // pixel array offset
        view.setUint32(14, 108, true); // BITMAPV4HEADER
        view.setInt32(18, width, true);
        view.setInt32(22, -height, true); // negative: rows top-down, as getImageData
        view.setUint16(26, 1, true); // planes
        view.setUint16(28, 32, true); // bits per pixel
        view.setUint32(30, 3, true); // BI_BITFIELDS
        view.setUint32(34, pixels.length, true);
        view.setInt32(38, 2835, true); // 72 dpi
        view.setInt32(42, 2835, true);
        view.setUint32(54, 0x000000ff, true); // red: byte 0
        view.setUint32(58, 0x0000ff00, true); // green: byte 1
        view.setUint32(62, 0x00ff0000, true); // blue: byte 2
        view.setUint32(66, 0xff000000, true); // alpha: byte 3
        view.setUint32(70, 0x73524742, true); // LCS_sRGB, as a canvas is
        return new Blob([header, pixels], { type: 'image/bmp' });
    }

    function releasePortraitSample(url) {
        if (!portraitSampleImages.has(url)) return;
        portraitSampleImages.delete(url);
        try { URL.revokeObjectURL(url); } catch (_) { /* already gone */ }
    }

    function portraitSampleShown(url) {
        return Array.from(document.querySelectorAll('.player-portrait, .portrait-art, img'))
            .some((element) => (element.tagName === 'IMG'
                ? element.getAttribute('src') === url
                : String(element.getAttribute('style') || '').includes(url)));
    }

    function paintIsSample(paint) {
        const value = String(paint || '');
        if (value.includes('data:')) return true;
        for (const url of portraitSampleImages.keys()) {
            if (value.includes(url)) return true;
        }
        return false;
    }

    /* ONLY AN ENGINE THAT READS THE BITMAP BACK EXACTLY IS GIVEN IT (audit of
       round 16, 28 September 2026). Chromium's, Firefox's and Linux WebKit's
       own BMP readers were measured; Safari and every iOS browser decode
       image/bmp through Apple's ImageIO, which never was. A decoder that
       swapped the channels, flipped the rows or dropped the alpha would paint
       every portrait wrong with nothing to notice it. So the page asks its
       engine once, while its first portrait is still decoding, with a 2x2
       bitmap of the same header: read back through a canvas it must come out
       as the pixels it was made of. An engine that does not gets the PNG of
       the same pixels, main's path pixel for pixel, at main's cost. Measured
       on real art (600 px sample): the bitmap takes 9 ms in Chrome (20 at 4x
       CPU), 6 in Firefox and 9 in WebKit; the PNG 21 (73), 25 and 98 — WebKit
       encodes it on the main thread even through toBlob. */
    const BITMAP_PROBE_PIXELS = [
        255, 0, 0, 255, 0, 255, 0, 255,
        0, 0, 255, 255, 240, 200, 40, 128,
    ];
    /* A translucent pixel read back through a premultiplied canvas rounds by a level or two. */
    const BITMAP_PROBE_TOLERANCE = 4;
    let bitmapReadBack = null;

    function engineReadsBitmapsBack() {
        if (bitmapReadBack) return bitmapReadBack;
        bitmapReadBack = new Promise((resolve) => {
            let url = null;
            const finish = (exact) => {
                if (url) {
                    try { URL.revokeObjectURL(url); } catch (_) { /* already gone */ }
                }
                resolve(exact);
            };
            try {
                const pixels = new Uint8ClampedArray(BITMAP_PROBE_PIXELS);
                url = URL.createObjectURL(portraitBitmapBlob(pixels, 2, 2));
                const image = document.createElement('img');
                const readBack = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = 2;
                        canvas.height = 2;
                        const context = canvas.getContext('2d', { willReadFrequently: true });
                        context.drawImage(image, 0, 0);
                        const read = context.getImageData(0, 0, 2, 2).data;
                        finish(pixels.every((value, index) => Math.abs(read[index] - value) <= BITMAP_PROBE_TOLERANCE));
                    } catch (_) {
                        finish(false);
                    }
                };
                image.src = url;
                if (typeof image.decode === 'function') {
                    image.decode().then(readBack, () => finish(false));
                } else {
                    image.onload = readBack;
                    image.onerror = () => finish(false);
                }
            } catch (_) {
                finish(false);
            }
        });
        return bitmapReadBack;
    }

    /** Resolves the sample's blob: URL once its pixels are decoded, or null. */
    function encodePortraitSample(canvas) {
        if (!canvas || canvas === PORTRAIT_DIRECT_SAMPLE) return Promise.resolve(canvas || null);
        if (typeof URL?.createObjectURL !== 'function') return Promise.resolve(null);
        return engineReadsBitmapsBack().then((exact) => {
            let pixels;
            try {
                /* WebKit can silently draw no pixels from a loaded WebP,
                   including the shared sampler's decoded copy. Decode of the
                   resulting bitmap would still succeed and cache an empty
                   portrait forever. Reuse the BMP export's existing read;
                   PNG needs only the full-bleed portrait's middle alpha row. */
                pixels = canvas.getContext('2d').getImageData(
                    0,
                    exact ? 0 : Math.floor(canvas.height / 2),
                    canvas.width,
                    exact ? canvas.height : 1,
                ).data; // throws if tainted
            } catch (_) {
                return null;
            }
            let hasPaint = false;
            for (let i = 3; i < pixels.length; i += 4) {
                if (pixels[i] === 0) continue;
                hasPaint = true;
                break;
            }
            if (!hasPaint) return null;
            if (!exact) return pngPortraitSample(canvas);
            return decodedPortraitSample(portraitBitmapBlob(pixels, canvas.width, canvas.height));
        });
    }

    /* The same pixels as a PNG, for an engine that cannot be given the
       bitmap; a PNG that never comes back is dropped at the load cap. */
    function pngPortraitSample(canvas) {
        return new Promise((resolve) => {
            let settled = false;
            let timer = null;
            const finish = (blob) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(blob ? decodedPortraitSample(blob) : null);
            };
            timer = setTimeout(() => finish(null), PORTRAIT_IMAGE_LOAD_TIMEOUT_MS);
            try {
                canvas.toBlob(finish, 'image/png'); // throws if tainted
            } catch (_) {
                finish(null);
            }
        });
    }

    function decodedPortraitSample(blob) {
        let url;
        try {
            url = URL.createObjectURL(blob);
        } catch (_) {
            return Promise.resolve(null);
        }
        const image = document.createElement('img');
        portraitSampleImages.set(url, image);
        return new Promise((resolve) => {
            let settled = false;
            const finish = (ok) => {
                if (settled) return;
                settled = true;
                if (!ok) releasePortraitSample(url);
                resolve(ok ? url : null);
            };
            image.decoding = 'async';
            image.src = url;
            if (typeof image.decode === 'function') {
                image.decode().then(() => finish(true), () => finish(false));
            } else {
                image.onload = () => finish(true);
                image.onerror = () => finish(false);
            }
        });
    }

    function retireOffDomImage(image, { clearSource = false } = {}) {
        if (!image) return;
        image.onload = null;
        image.onerror = null;
        if (!clearSource) return;
        if (typeof image.removeAttribute === 'function') image.removeAttribute('src');
        else image.src = '';
    }

    /* The stepped reduction itself, from pixels already decoded: the canvas
       holding the sample, PORTRAIT_DIRECT_SAMPLE when the source is already
       small enough, or null when it cannot be drawn.

       SAME PIXELS AS SHOWN, NEVER A SMALLER RENDITION. "Downsample" is the
       reduction of the ~2752 px source to the sampler's display-size raster:
       getPortraitSample asks for twice the portrait's device pixels (at least
       PORTRAIT_MIN_SAMPLE_WIDTH), never more than the source has, so the
       browser only makes a small final reduction and never enlarges it. */
    function downsamplePortrait(img, spec) {
        try {
            let src = img;
            let w = img.naturalWidth;
            let h = img.naturalHeight;
            let targetWidth;
            let targetHeight;
            let cropSource = null;

            if (spec.crop) {
                const targetAspect = spec.width / spec.height;
                let cropWidth = w;
                let cropHeight = h;
                let left = 0;
                let top = 0;
                if (w / h > targetAspect) {
                    cropWidth = h * targetAspect;
                    left = (w - cropWidth) * spec.x;
                } else {
                    cropHeight = w / targetAspect;
                    top = (h - cropHeight) * spec.y;
                }
                cropSource = {
                    x: Math.round(left),
                    y: Math.round(top),
                    width: Math.max(1, Math.round(cropWidth)),
                    height: Math.max(1, Math.round(cropHeight)),
                };
                w = cropSource.width;
                h = cropSource.height;
                targetWidth = Math.min(w, spec.width);
                targetHeight = Math.min(h, spec.height);
            } else {
                const heightDrivenWidth = spec.height * (w / h);
                const unclampedWidth = Math.ceil(Math.max(spec.width, heightDrivenWidth) / 64) * 64;
                targetWidth = Math.min(w, unclampedWidth);
                targetHeight = Math.max(1, Math.round(h * (targetWidth / w)));
            }

            if (w <= targetWidth * 1.3 && h <= targetHeight * 1.3) {
                return PORTRAIT_DIRECT_SAMPLE;
            }
            /* Hand the engine the largest reduction it handles well in
               one draw and step only above that: below ~8x a single
               high-quality draw keeps more detail, above it the
               one-shot filter aliases. Same probe and limit as the
               card-art sampler. */
            const highQuality = !!(window.MoonfallRasterSampler
                && window.MoonfallRasterSampler.supportsHighQualityDownscale
                && window.MoonfallRasterSampler.supportsHighQualityDownscale());
            const oneDrawLimit = highQuality ? 8 : 2;
            while (w > targetWidth * oneDrawLimit || h > targetHeight * oneDrawLimit) {
                const nw = Math.max(targetWidth, Math.round(w / 2));
                const nh = Math.max(targetHeight, Math.round(h / 2));
                const step = document.createElement('canvas');
                step.width = nw;
                step.height = nh;
                const sctx = step.getContext('2d');
                sctx.imageSmoothingEnabled = true;
                sctx.imageSmoothingQuality = 'high';
                if (cropSource) {
                    sctx.drawImage(
                        img,
                        cropSource.x,
                        cropSource.y,
                        cropSource.width,
                        cropSource.height,
                        0,
                        0,
                        nw,
                        nh
                    );
                    cropSource = null;
                } else {
                    sctx.drawImage(src, 0, 0, nw, nh);
                }
                src = step;
                w = nw;
                h = nh;
            }
            const out = document.createElement('canvas');
            out.width = targetWidth;
            out.height = targetHeight;
            const octx = out.getContext('2d');
            octx.imageSmoothingEnabled = true;
            octx.imageSmoothingQuality = 'high';
            if (cropSource) {
                octx.drawImage(
                    img,
                    cropSource.x,
                    cropSource.y,
                    cropSource.width,
                    cropSource.height,
                    0,
                    0,
                    targetWidth,
                    targetHeight
                );
            } else {
                octx.drawImage(src, 0, 0, targetWidth, targetHeight);
            }
            return out;
        } catch (_) {
            return null;
        }
    }

    function buildPortraitDownsample(url, spec) {
        const cacheKey = `${spec.width}x${spec.height}:${spec.crop ? `${spec.x}:${spec.y}` : 'full'}:${url}`;
        const cached = portraitDownsampleCache.get(cacheKey);
        if (cached) return cached;
        // Answered while the portrait itself is still decoding.
        void engineReadsBitmapsBack();

        const task = new Promise((resolve) => {
            /* ONE DOWNLOAD PER PORTRAIT. The match warm has usually decoded
               this picture already (AssetPreloader -> the raster sampler); a
               private Image asked the art host for it again, and a host that
               varies by Origin answers a second request with a second
               download (measured 25 September 2026: each ~835 KB portrait
               three times on a phone's first visit). Sample the shared pixels,
               or load them once, through the sampler. */
            const sampler = window.MoonfallRasterSampler;
            if (
                typeof sampler?.getDecodedImage === 'function'
                && typeof sampler?.prepareDecodedImage === 'function'
            ) {
                let sharedSettled = false;
                let sharedTimer = null;
                const settleShared = (value) => {
                    if (sharedSettled) return;
                    sharedSettled = true;
                    if (sharedTimer !== null) clearTimeout(sharedTimer);
                    resolve(value);
                };
                sharedTimer = setTimeout(() => settleShared(null), PORTRAIT_IMAGE_LOAD_TIMEOUT_MS);
                const decoded = sampler.getDecodedImage(url);
                const ready = decoded
                    ? Promise.resolve(decoded)
                    : Promise.resolve(sampler.prepareDecodedImage(url))
                        .then((ok) => (ok ? sampler.getDecodedImage(url) : null), () => null);
                ready
                    .then((image) => encodePortraitSample(image ? downsamplePortrait(image, spec) : null))
                    .then((value) => {
                        // Too late: the cap already answered for this sample.
                        if (sharedSettled && typeof value === 'string') releasePortraitSample(value);
                        settleShared(value);
                    }, () => settleShared(null));
                return;
            }
            const img = new Image();
            let settled = false;
            let loadTimer = null;
            const settle = (value) => {
                if (settled) {
                    if (typeof value === 'string') releasePortraitSample(value);
                    return;
                }
                settled = true;
                if (loadTimer !== null) clearTimeout(loadTimer);
                retireOffDomImage(img, { clearSource: !value });
                resolve(value);
            };
            img.crossOrigin = 'anonymous';
            img.decoding = 'async';
            img.onload = () => {
                encodePortraitSample(downsamplePortrait(img, spec)).then(settle, () => settle(null));
            };
            img.onerror = () => settle(null);
            loadTimer = setTimeout(() => settle(null), PORTRAIT_IMAGE_LOAD_TIMEOUT_MS);
            img.src = url;
        });
        cachePortraitDownsample(cacheKey, task);
        task.then((sampleUrl) => {
            if (!sampleUrl && portraitDownsampleCache.get(cacheKey) === task) {
                portraitDownsampleCache.delete(cacheKey);
            }
        });
        return task;
    }

    /* The direct picture decoded as a CSS background asks for it (no CORS),
       within the load cap; resolves either way, never rejects. */
    function decodedDirectPortrait(url) {
        return new Promise((resolve) => {
            const image = new Image();
            let settled = false;
            let timer = null;
            const finish = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                retireOffDomImage(image);
                resolve();
            };
            timer = setTimeout(finish, PORTRAIT_IMAGE_LOAD_TIMEOUT_MS);
            image.decoding = 'async';
            image.src = url;
            if (typeof image.decode === 'function') {
                image.decode().then(finish, finish);
            } else {
                image.onload = finish;
                image.onerror = finish;
            }
        });
    }

    // Saved-deck tiles deliberately keep their accepted full-image background
    // composition. Decode the authoritative source before the tile enters the
    // DOM, then keep that URL unchanged. Chromium softens the canvas reduction
    // used by the generic portrait enhancer at this size, while its image
    // decoder keeps the same source crisp.
    function preparePortraitBackground(url) {
        try {
            if (!url || url.startsWith('data:')) {
                return Promise.resolve(url || '');
            }
            const cached = portraitBackgroundPreloadCache.get(url);
            if (cached) return cached;

            const task = new Promise((resolve) => {
                const image = new Image();
                let settled = false;
                let timer = null;
                const finish = (loaded) => {
                    if (settled) return;
                    settled = true;
                    if (timer !== null) clearTimeout(timer);
                    retireOffDomImage(image, { clearSource: !loaded });
                    resolve(url);
                };
                image.decoding = 'async';
                image.onload = () => finish(true);
                image.onerror = () => finish(false);
                timer = setTimeout(() => finish(false), PORTRAIT_IMAGE_LOAD_TIMEOUT_MS);
                image.src = url;
            });
            portraitBackgroundPreloadCache.set(url, task);
            if (portraitBackgroundPreloadCache.size > PORTRAIT_DOWNSAMPLE_CACHE_LIMIT) {
                portraitBackgroundPreloadCache.delete(
                    portraitBackgroundPreloadCache.keys().next().value
                );
            }
            return task;
        } catch (_) {
            return Promise.resolve(url || '');
        }
    }

    // Portrait art is a very high-res source (~2752px) shown at 46-300px. A
    // single Chromium reduction of that much fine detail aliases fur and leaves
    // into blocky speckle. Pre-resample with stepped high-quality canvases —
    // proper anti-aliasing, not blur — then let the browser make only a small
    // final reduction. The direct URL remains visible on every failure path.
    function getPortraitSample(element, url) {
        const rect = element.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const isImage = element.tagName === 'IMG';
        const width = rect.width || 144;
        const height = rect.height || width;
        const scale = Math.max(2 * dpr, PORTRAIT_MIN_SAMPLE_WIDTH / width);
        const targetWidth = Math.max(1, Math.ceil(width * scale));
        const targetHeight = Math.max(1, Math.ceil(height * scale));
        const style = isImage ? getComputedStyle(element) : null;
        const [rawX = '50%', rawY = '50%'] = String(style?.objectPosition || '').split(/\s+/);
        const position = (value, fallback) => {
            const match = value.match(/^(-?\d+(?:\.\d+)?)%$/);
            return match ? Number(match[1]) / 100 : fallback;
        };
        const spec = {
            width: targetWidth,
            height: targetHeight,
            crop: isImage && style.objectFit === 'cover',
            x: position(rawX, 0.5),
            y: position(rawY, 0.5),
        };
        return {
            isImage,
            spec,
            key: `${url}:${targetWidth}x${targetHeight}:${spec.crop ? `${spec.x}:${spec.y}` : 'full'}`,
        };
    }

    function enhancePortraitImage(element, url) {
        try {
            if (!element || !url || url.startsWith('data:')) {
                return Promise.resolve(false);
            }
            ensurePortraitResizeObserver(element);
            const { isImage, spec, key: sampleKey } = getPortraitSample(element, url);
            const activeTask = portraitRenderTasks.get(element);
            if (
                element.dataset.portraitSrc === url &&
                element.dataset.portraitFailedSampleKey === sampleKey &&
                Number(element.dataset.portraitSampleRetryAt || 0) > Date.now()
            ) {
                return Promise.resolve(false);
            }
            if (
                element.dataset.portraitSrc === url &&
                element.dataset.portraitSampleKey === sampleKey
            ) {
                if (activeTask) return activeTask;
                if (
                    element.dataset.portraitQuality === 'enhanced' ||
                    element.dataset.portraitQuality === 'direct'
                ) {
                    return Promise.resolve(element.dataset.portraitQuality === 'enhanced');
                }
            }

            const previousPaint = getPortraitPaint(element);
            const previousSampleKey = element.dataset.portraitSampleKey;
            const previousRenderedSampleKey = element.dataset.portraitRenderedSampleKey;
            element.dataset.portraitSrc = url;
            const requestId = String(++portraitRenderRequestId);
            element.dataset.portraitRenderRequest = requestId;
            element.dataset.portraitSampleKey = sampleKey;
            element.dataset.portraitQuality = 'pending';
            delete element.dataset.portraitFailedSampleKey;
            delete element.dataset.portraitSampleRetryAt;
            if (isImage) element.dataset.originalPortraitSrc = url;

            const markSampleFailure = () => {
                element.dataset.portraitFailedSampleKey = sampleKey;
                element.dataset.portraitSampleRetryAt = String(Date.now() + PORTRAIT_RETRY_DELAY_MS);
            };

            const retainOrRestoreDirect = () => {
                // A data: (an older sample) or one of this module's blob:
                // samples is enhanced pixels worth keeping over the direct URL.
                const hasEnhancedPixels = paintIsSample(previousPaint);
                if (hasEnhancedPixels) {
                    if (isImage) element.src = previousPaint;
                    element.dataset.portraitSampleKey =
                        previousRenderedSampleKey || previousSampleKey || '';
                    element.dataset.portraitQuality = 'enhanced';
                    return;
                }
                setPortraitPaintResult(element, url);
                delete element.dataset.portraitSampleKey;
                delete element.dataset.portraitRenderedSampleKey;
                element.dataset.portraitQuality = 'direct';
            };

            const stillCurrent = () => element.isConnected
                && element.dataset.portraitSrc === url
                && element.dataset.portraitRenderRequest === requestId;

            const task = buildPortraitDownsample(url, spec).then((sampleUrl) => {
                if (!stillCurrent()) return false;
                if (!sampleUrl) {
                    /* A portrait with nothing on it yet gets the direct
                       picture decoded first, as a sample is: painted at
                       once, a CSS background still on the wire filled in
                       in front of the player (it cannot share the sample's
                       CORS download). The board waits for it within its
                       own cap, as it waits for a sample. */
                    const blank = !isImage && !getPortraitPaint(element) && !paintIsSample(previousPaint);
                    return (blank ? decodedDirectPortrait(url) : Promise.resolve()).then(() => {
                        if (!stillCurrent()) return false;
                        retainOrRestoreDirect();
                        markSampleFailure();
                        return false;
                    });
                }
                if (sampleUrl === PORTRAIT_DIRECT_SAMPLE) {
                    setPortraitPaintResult(element, url);
                    element.dataset.portraitRenderedSampleKey = sampleKey;
                    element.dataset.portraitQuality = 'direct';
                    delete element.dataset.portraitFailedSampleKey;
                    delete element.dataset.portraitSampleRetryAt;
                    return false;
                }
                setPortraitPaintResult(element, sampleUrl);
                element.dataset.portraitRenderedSampleKey = sampleKey;
                element.dataset.portraitQuality = 'enhanced';
                delete element.dataset.portraitFailedSampleKey;
                delete element.dataset.portraitSampleRetryAt;
                return true;
            }).catch(() => {
                if (
                    element.dataset.portraitSrc === url &&
                    element.dataset.portraitRenderRequest === requestId
                ) {
                    retainOrRestoreDirect();
                    markSampleFailure();
                }
                return false;
            }).finally(() => {
                if (portraitRenderTasks.get(element) === task) {
                    portraitRenderTasks.delete(element);
                }
            });
            portraitRenderTasks.set(element, task);
            return task;
        } catch (_) {
            return Promise.resolve(false);
        }
    }

    function clearPortraitSourceLoader(element) {
        const record = portraitSourceLoaders.get(element);
        if (!record) return;
        if (record.bootstrapTimer !== null) clearTimeout(record.bootstrapTimer);
        Cards?.disposeCardArtImage?.(record.probe);
        record.probe.remove();
        portraitSourceLoaders.delete(element);
    }

    function clearAllPortraitSourceLoaders() {
        Array.from(portraitSourceLoaders.keys()).forEach(clearPortraitSourceLoader);
    }

    function applyProfilePicture(element, value, preferredDirectUrl) {
        if (!element) return;
        const { directUrl, assetId } = resolveProfilePictureRequest(value, preferredDirectUrl);
        const requestKey = directUrl || assetId || '';
        const requestUnchanged = element.dataset.portraitRequestKey === requestKey;

        if (requestUnchanged) {
            if (portraitSourceLoaders.has(element)) return;
            if (element.dataset.portraitResolutionState === 'resolved' && element.dataset.portraitSrc) {
                ensurePortraitResizeObserver(element);
                if (!getPortraitPaint(element)) setPortraitPaint(element, element.dataset.portraitSrc);
                enhancePortraitImage(element, element.dataset.portraitSrc);
                return;
            }
            // Teardown retires the shared loader but intentionally leaves the
            // portrait nodes in place. Re-entering a battle with the same
            // summoner identity must create a fresh owner instead of treating
            // the old `retrying` dataset as an active request.
        }

        clearPortraitSourceLoader(element);
        element.dataset.portraitRequestKey = requestKey;
        element.dataset.portraitRenderRequest = `source-${++portraitRenderRequestId}`;
        delete element.dataset.portraitSrc;
        delete element.dataset.portraitSampleKey;
        delete element.dataset.portraitRenderedSampleKey;
        delete element.dataset.portraitFailedSampleKey;
        delete element.dataset.portraitSampleRetryAt;
        clearPortraitPaint(element);

        if (!requestKey) {
            element.dataset.portraitResolutionState = 'unavailable';
            element.dataset.portraitQuality = 'unavailable';
            element.classList.remove('portrait-art-retrying');
            element.classList.add('portrait-art-unavailable');
            return;
        }

        element.dataset.portraitResolutionState = 'retrying';
        element.dataset.portraitQuality = 'pending';
        element.classList.remove('portrait-art-unavailable');
        element.classList.add('portrait-art-retrying');
        const probe = document.createElement('img');
        probe.className = 'portrait-source-probe';
        probe.alt = '';
        probe.setAttribute('aria-hidden', 'true');
        element.appendChild(probe);

        const record = { requestKey, probe, bootstrapTimer: null };
        portraitSourceLoaders.set(element, record);
        const ownsRequest = () => (
            portraitSourceLoaders.get(element) === record
            && element.isConnected
            && element.dataset.portraitRequestKey === requestKey
        );
        const startSharedLoader = () => {
            record.bootstrapTimer = null;
            if (!ownsRequest()) {
                if (portraitSourceLoaders.get(element) === record) {
                    clearPortraitSourceLoader(element);
                }
                return;
            }
            const resolveArt = Cards?.resolveCardArt;
            if (typeof resolveArt !== 'function') {
                record.bootstrapTimer = setTimeout(startSharedLoader, 1000);
                return;
            }
            const readiness = resolveArt({
                id: '',
                picture: assetId,
                imageUrl: directUrl || '',
            }, probe, { directArtOnly: true, rasterReserveScale: 1 });
            void Promise.resolve(readiness).then((ready) => {
                if (!ownsRequest()) {
                    if (portraitSourceLoaders.get(element) === record) {
                        clearPortraitSourceLoader(element);
                    }
                    return;
                }
                if (ready !== true) {
                    record.bootstrapTimer = setTimeout(startSharedLoader, 1000);
                    return;
                }
                const resolvedUrl = sanitizeDirectMediaUrl(
                    probe.dataset.cardArtSource || probe.getAttribute('src')
                );
                if (!resolvedUrl) {
                    record.bootstrapTimer = setTimeout(startSharedLoader, 1000);
                    return;
                }
                clearPortraitSourceLoader(element);
                element.dataset.portraitResolutionState = 'resolved';
                setPortraitBackground(element, resolvedUrl);
                element.dispatchEvent(new CustomEvent('moonfall:portrait-art-settled', {
                    bubbles: true,
                    detail: { source: resolvedUrl },
                }));
            });
        };
        startSharedLoader();
    }

    window[portraitRuntimeKey] = { dispose: clearAllPortraitSourceLoaders };

    /**
     * Setup all game indicators directly
     * @param {Object} data - Game state data
     */
    function setupIndicators(data) {
        if (!data || !data.gameState) {
            console.error('Invalid data received for setting up indicators.');
            return;
        }

        const gameState = data.gameState;
        const playerArea = document.getElementById('player-area');
        const opponentArea = document.getElementById('opponent-area');

        if (!playerArea || !opponentArea) return;

        // ===== PLAYER PORTRAIT =====
        let playerPortrait = document.getElementById('player-portrait');
        if (!playerPortrait) {
            playerPortrait = document.createElement('div');
            playerPortrait.id = 'player-portrait';
            playerPortrait.className = 'player-portrait';
            playerArea.appendChild(playerPortrait);
            applyProfilePicture(playerPortrait, gameState.player.profilePicture, gameState.player.profilePictureUrl);
        } else {
            // Ensure portrait reflects the latest character selection on every update
            applyProfilePicture(playerPortrait, gameState.player.profilePicture, gameState.player.profilePictureUrl);
        }
        updateSummonerNameplate(playerPortrait, 'player', gameState.player?.name);

        // ===== OPPONENT PORTRAIT =====
        let opponentPortrait = document.getElementById('opponent-portrait');
        if (!opponentPortrait) {
            opponentPortrait = document.createElement('div');
            opponentPortrait.id = 'opponent-portrait';
            opponentPortrait.className = 'player-portrait';
            opponentArea.appendChild(opponentPortrait);

            // Defensive check for opponent data during reconnection
            const profilePicture = gameState.opponent?.profilePicture || '';

            applyProfilePicture(opponentPortrait, profilePicture, gameState.opponent?.profilePictureUrl);
        } else {
            const profilePicture = gameState.opponent?.profilePicture || '';
            applyProfilePicture(opponentPortrait, profilePicture, gameState.opponent?.profilePictureUrl);
        }
        updateSummonerNameplate(opponentPortrait, 'opponent', gameState.opponent?.name);

        // Health and other counters may tick through shared FX.animateCount
        // with a brief pulse. Mana calls this with `animate: false`: its
        // current/total state is synchronous and visually neutral unless the
        // player deliberately inspects the socket. The previous value rides
        // on data-count-value (mana text "2/3" is not parseable).
        function setNumberDisplay(el, value, { render, animate = true } = {}) {
            if (!el) return;
            const fmt = typeof render === 'function' ? render : String;
            const next = Number(value);
            const prev = Number(el.dataset.countValue);
            el.dataset.countValue = String(next);
            if (
                animate
                && Number.isFinite(prev)
                && Number.isFinite(next)
                && prev !== next
                && FX?.animateCount
            ) {
                FX.animateCount(el, prev, next, { duration: 450, render });
                // The tick owns the number until it lands (see below).
                el.dataset.countTickUntil = String(Date.now() + 450 + 50);
                Effects?.withTempClass?.(el, 'count-pulse', 500);
            } else if (prev === next && Number(el.dataset.countTickUntil) > Date.now()) {
                /* A repeat of the value a running tick is heading to (the second
                   post-keep 'playing' broadcast): the tick ends on it. Writing it
                   now let the tick's next frame write its middle number back,
                   13 -> 12 -> 13 -> 12 on the deck count (28 September 2026). */
            } else {
                delete el.dataset.countTickUntil;
                el.textContent = fmt(next);
            }
        }

        // ===== HEALTH INDICATORS =====
        // Player health indicator
        let playerHealth = document.getElementById('player-health-indicator');
        if (!playerHealth && playerPortrait) {
            playerHealth = document.createElement('div');
            playerHealth.id = 'player-health-indicator';
            playerHealth.className = 'health-indicator';
            playerHealth.style.width = '59px';
            playerHealth.style.height = '59px';
            playerPortrait.appendChild(playerHealth);
        }
        if (playerHealth) setNumberDisplay(playerHealth, gameState.player.health);

        // Opponent health indicator
        let opponentHealth = document.getElementById('opponent-health-indicator');
        if (!opponentHealth && opponentPortrait) {
            opponentHealth = document.createElement('div');
            opponentHealth.id = 'opponent-health-indicator';
            opponentHealth.className = 'health-indicator';
            opponentHealth.style.width = '59px';
            opponentHealth.style.height = '59px';
            opponentPortrait.appendChild(opponentHealth);
        }
        // Add defensive null check for opponent health
        if (opponentHealth && gameState.opponent && typeof gameState.opponent.health !== 'undefined') {
            setNumberDisplay(opponentHealth, gameState.opponent.health);
        }

        // ===== STATUS INDICATORS =====
        // Player status indicator
        const playerStatus = (gameState.player.status || '').trim();
        renderStatusIndicator({
            id: 'player-status-indicator',
            portrait: playerPortrait,
            status: playerStatus,
            labelKey: 'game.yourStatus',
            labelFallback: 'You: {{status}}',
        });

        // Opponent status indicator
        const opponentStatus = (gameState.opponent && gameState.opponent.status || '').trim();
        renderStatusIndicator({
            id: 'opponent-status-indicator',
            portrait: opponentPortrait,
            status: opponentStatus,
            labelKey: 'game.opponentStatus',
            labelFallback: 'Opponent: {{status}}',
        });

        // ===== MANA INDICATORS =====
        // Player mana indicator (attached to portrait)
        let playerMana = document.getElementById('player-mana-indicator');
        if (!playerMana) {
            playerMana = document.createElement('div');
            playerMana.id = 'player-mana-indicator';
            playerMana.className = 'mana-indicator';
            playerMana.style.width = '59px';
            playerMana.style.height = '59px';

            // Get player portrait to attach the mana indicator to it
            const playerPortrait = document.getElementById('player-portrait');
            if (playerPortrait) {
                playerPortrait.appendChild(playerMana);
            } else {
                // Fallback to player area if portrait not found
                playerArea.appendChild(playerMana);
                console.warn('Player portrait not found, attaching mana indicator to player area instead');
            }
        }
        if (playerMana) {
            bindManaPressFeedback(playerMana);
            const current = gameState.player && typeof gameState.player.mana !== 'undefined' ? gameState.player.mana : 0;
            const total = gameState.player && typeof gameState.player.totalMana !== 'undefined' ? gameState.player.totalMana : current;
            setNumberDisplay(playerMana, current, {
                render: (v) => `${v}/${total}`,
                animate: false,
            });
        }

        // Opponent mana indicator (attached to portrait)
        let opponentMana = document.getElementById('opponent-mana-indicator');
        if (!opponentMana) {
            opponentMana = document.createElement('div');
            opponentMana.id = 'opponent-mana-indicator';
            opponentMana.className = 'mana-indicator';
            opponentMana.style.width = '59px';
            opponentMana.style.height = '59px';

            // Get opponent portrait to attach the mana indicator to it
            const opponentPortrait = document.getElementById('opponent-portrait');
            if (opponentPortrait) {
                opponentPortrait.appendChild(opponentMana);
            } else {
                // Fallback to opponent area if portrait not found
                opponentArea.appendChild(opponentMana);
                console.warn('Opponent portrait not found, attaching mana indicator to opponent area instead');
            }
        }
        // Add defensive null check for opponent mana during reconnection
        if (opponentMana && gameState.opponent && typeof gameState.opponent.mana !== 'undefined') {
            bindManaPressFeedback(opponentMana);
            const current = gameState.opponent.mana;
            const total = typeof gameState.opponent.totalMana !== 'undefined' ? gameState.opponent.totalMana : current;
            setNumberDisplay(opponentMana, current, {
                render: (v) => `${v}/${total}`,
                animate: false,
            });
        }

        // ===== DECK VISUALS =====
        // Player deck - ensuring consistent sizing with cards
        if (!playerDeckCreated) {
            // Create a wrapper similar to hand-card-wrapper to ensure consistent styling context
            const playerDeckWrapper = document.createElement('div');
            playerDeckWrapper.id = 'player-deck-wrapper';

            // Make sure wrapper doesn't constrain height
            playerDeckWrapper.style.overflow = 'visible';

            // Create the deck visual with the same class structure as cards
            const playerDeck = document.createElement('div');
            playerDeck.id = 'player-deck';
            playerDeck.className = 'deck-visual';

            // Add deck count indicator
            const playerDeckCount = document.createElement('div');
            playerDeckCount.className = 'deck-count';
            playerDeckCount.textContent = gameState.player.deckSize;
            playerDeckCount.dataset.countValue = String(gameState.player.deckSize);

            // Append in the same hierarchy as cards
            playerDeck.appendChild(playerDeckCount);
            playerDeckWrapper.appendChild(playerDeck);
            playerArea.appendChild(playerDeckWrapper);
            playerDeckCreated = true;
        } else {
            const deckCount = document.querySelector('#player-deck .deck-count');
            if (deckCount) setNumberDisplay(deckCount, gameState.player.deckSize);
        }

        // Opponent deck - ensuring consistent sizing with cards
        if (!opponentDeckCreated) {
            // Create a wrapper similar to hand-card-wrapper to ensure consistent styling context
            const opponentDeckWrapper = document.createElement('div');
            opponentDeckWrapper.id = 'opponent-deck-wrapper';

            // Make sure wrapper doesn't constrain height
            opponentDeckWrapper.style.overflow = 'visible';

            // Create the deck visual with the same class structure as cards
            const opponentDeck = document.createElement('div');
            opponentDeck.id = 'opponent-deck';
            opponentDeck.className = 'deck-visual';

            // Add deck count indicator
            const opponentDeckCount = document.createElement('div');
            opponentDeckCount.className = 'deck-count';
            opponentDeckCount.textContent = gameState.opponent.deckSize;
            opponentDeckCount.dataset.countValue = String(gameState.opponent.deckSize);

            // Append in the same hierarchy as cards
            opponentDeck.appendChild(opponentDeckCount);
            opponentDeckWrapper.appendChild(opponentDeck);
            opponentArea.appendChild(opponentDeckWrapper);
            opponentDeckCreated = true;
        } else {
            const deckCount = document.querySelector('#opponent-deck .deck-count');
            if (deckCount) setNumberDisplay(deckCount, gameState.opponent.deckSize);
        }

        // --- UPDATE DECK VISUALS (CARDBACKS) ---
        updateDeckCardbacks(gameState);
    }

    function applyDeckCardback(deck, selectedSlug) {
        if (!deck) return;
        const slug = typeof selectedSlug === 'string' && selectedSlug.trim()
            ? selectedSlug.trim()
            : 'default';
        if (deck.dataset.cardbackArtSlug === slug) return;
        const resolver = CardbackArt;
        if (typeof resolver?.apply === 'function') {
            resolver.apply(deck, slug);
            return;
        }
        // A missing optional module may cost recovery, never truthfulness.
        // Only an explicit default selection is allowed to display stock.
        CardBackRaster?.clear?.(deck);
        deck.dataset.cardbackArtSlug = slug;
        deck.style.setProperty(
            '--cardback-image',
            slug === 'default' ? 'url("/assets/images/cardback.webp")' : 'none',
        );
    }

    function updateDeckCardbacks() {
        const gameState = (typeof GameState !== 'undefined' && GameState.get) ? GameState.get() : {};

        if (window.CLIENT_DEBUG_MODE) {
            console.log('[Indicators] updateDeckCardbacks called', {
                hasPlayer: !!gameState.player,
                playerCardback: gameState.player?.selectedCardback,
                hasUser: !!gameState.user,
                userCardback: gameState.user?.selectedCardback,
                hasOpponent: !!gameState.opponent,
                opponentCardback: gameState.opponent?.selectedCardback,
            });
        }

        // Player Deck
        const playerDeck = document.getElementById('player-deck');
        const selectedBack = gameState.player?.selectedCardback || gameState.user?.selectedCardback;
        applyDeckCardback(playerDeck, selectedBack);

        // Opponent Deck
        const opponentDeck = document.getElementById('opponent-deck');
        applyDeckCardback(opponentDeck, gameState.opponent?.selectedCardback);
    }

    // Setup portrait as drop targets
    function setupPortraitDropTargets() {
        const playerPortrait = document.getElementById('player-portrait');
        const opponentPortrait = document.getElementById('opponent-portrait');

        // Clean up old hitbox overlays if they exist
        document.querySelectorAll('.portrait-hitbox').forEach(el => el.remove());

        if (playerPortrait) {
            CardDrag.registerTarget(playerPortrait, {
                zone: 'player-portrait',
                validate: (d) => d && GameState.get().isYourTurn,
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    return routeActionDrop(d, null, s.playerId);
                }
            });
        }

        if (opponentPortrait) {
            CardDrag.registerTarget(opponentPortrait, {
                zone: 'opponent-portrait',
                validate: (d) => d && GameState.get().isYourTurn,
                // No drop-time cue: the summoner must not flinch before the
                // blow lands. The strike is drawn on the server's accept.
                onDrop: (d) => {
                    const s = GameState.get();
                    if (s.actionInProgress) return;
                    return routeActionDrop(d, null, s.opponentId);
                }
            });
        }
    }

    /**
     * Normalize existing deck wrappers and return dimensions to responsive CSS.
     * Hot-reloaded clients may still carry the old inline card-sized values;
     * removing them lets the phone rail and desktop card variables both win.
     */
    function updateExistingDeckSizes() {
        // Update player deck if it exists
        const playerDeck = document.getElementById('player-deck');
        if (playerDeck) {
            // If deck is not in a wrapper yet, create one
            if (playerDeck.parentElement.id !== 'player-deck-wrapper') {
                const playerDeckWrapper = document.createElement('div');
                playerDeckWrapper.id = 'player-deck-wrapper';

                const parentElement = playerDeck.parentElement;

                // Move the deck to the wrapper
                playerDeck.parentElement.removeChild(playerDeck);
                playerDeckWrapper.appendChild(playerDeck);
                parentElement.appendChild(playerDeckWrapper);
            }

            playerDeck.style.removeProperty('width');
            playerDeck.style.removeProperty('height');
        }

        // Update opponent deck if it exists
        const opponentDeck = document.getElementById('opponent-deck');
        if (opponentDeck) {
            // If deck is not in a wrapper yet, create one
            if (opponentDeck.parentElement.id !== 'opponent-deck-wrapper') {
                const opponentDeckWrapper = document.createElement('div');
                opponentDeckWrapper.id = 'opponent-deck-wrapper';

                const parentElement = opponentDeck.parentElement;

                // Move the deck to the wrapper
                opponentDeck.parentElement.removeChild(opponentDeck);
                opponentDeckWrapper.appendChild(opponentDeck);
                parentElement.appendChild(opponentDeckWrapper);
            }

            opponentDeck.style.removeProperty('width');
            opponentDeck.style.removeProperty('height');
        }
    }

    /**
     * Debug function to directly compare deck and card sizing
     * This can be called from the browser console with: Indicators.debugDeckCardSizing()
     */
    function debugDeckCardSizing() {
        if (window.CLIENT_DEBUG_MODE) console.log('DIRECT DECK VS CARD COMPARISON');

        // Get elements
        const playerDeck = document.getElementById('player-deck');
        const opponentDeck = document.getElementById('opponent-deck');
        const playerCard = document.querySelector('#player-hand .card');
        const opponentCard = document.querySelector('#opponent-hand .card');

        if (!playerDeck || !opponentDeck) {
            console.error('Decks not found in DOM');
            return;
        }

        // Get computed styles
        const playerDeckStyle = window.getComputedStyle(playerDeck);
        const opponentDeckStyle = window.getComputedStyle(opponentDeck);

        // Log deck dimensions
        if (window.CLIENT_DEBUG_MODE) {
            console.log('=== DECK DIMENSIONS ===');
            console.log('Player Deck:', playerDeckStyle.width, 'x', playerDeckStyle.height);
            console.log('Opponent Deck:', opponentDeckStyle.width, 'x', opponentDeckStyle.height);
        }

        // Log card dimensions if available
        if (playerCard) {
            const playerCardStyle = window.getComputedStyle(playerCard);
            if (window.CLIENT_DEBUG_MODE) console.log('\nPlayer Card:', playerCardStyle.width, 'x', playerCardStyle.height);
        }

        if (opponentCard) {
            const opponentCardStyle = window.getComputedStyle(opponentCard);
            if (window.CLIENT_DEBUG_MODE) console.log('Opponent Card:', opponentCardStyle.width, 'x', opponentCardStyle.height);
        }

        // Check for any inline styles
        if (window.CLIENT_DEBUG_MODE) {
            console.log('\n=== INLINE STYLES ===');
            console.log('Player Deck inline styles:', playerDeck.getAttribute('style'));
            console.log('Opponent Deck inline styles:', opponentDeck.getAttribute('style'));
        }

        if (playerCard) {
            if (window.CLIENT_DEBUG_MODE) console.log('Player Card inline styles:', playerCard.getAttribute('style'));
        }

        // Check for CSS rule specificity issues
        if (window.CLIENT_DEBUG_MODE) {
            console.log('\n=== CSS SPECIFICITY CHECK ===');
            console.log('Deck CSS class names:', playerDeck.className);
        }
        if (playerCard) {
            if (window.CLIENT_DEBUG_MODE) console.log('Card CSS class names:', playerCard.className);
        }

        // Check box-sizing which can affect dimensions
        if (window.CLIENT_DEBUG_MODE) {
            console.log('\n=== BOX SIZING CHECK ===');
            console.log('Player Deck box-sizing:', playerDeckStyle.boxSizing);
        }
        if (playerCard) {
            if (window.CLIENT_DEBUG_MODE) console.log('Player Card box-sizing:', window.getComputedStyle(playerCard).boxSizing);
        }

        // Check parent containers
        if (window.CLIENT_DEBUG_MODE) {
            console.log('\n=== PARENT CONTAINER CHECK ===');
            console.log('Player Deck parent:', playerDeck.parentElement.tagName, playerDeck.parentElement.id);
        }
        if (playerCard) {
            if (window.CLIENT_DEBUG_MODE) console.log('Player Card parent:', playerCard.parentElement.tagName, playerCard.parentElement.id);
        }
    }

    /* ----------------------------------------------------------------------
       PRESS STATE FOR TOUCH

       CSS `:active` is not a dependable press signal on a phone. iOS only
       applies it when a touch handler exists on the element or an ancestor,
       and a pointerdown that anything preventDefaults never activates at all —
       measured here as `#player-deck-wrapper:active` failing to match during a
       held press even though the wrapper, the deck inside it, `body.game-active`
       and the coarse-pointer media query were all correct. That is why "tapping
       summoner icons or decks has no effect" survived a previous fix built on
       `:active` alone.

       So the class is driven explicitly. `:active` stays in the stylesheet as a
       desktop nicety; `.mf-pressed` is what a finger actually gets. Delegated
       because the deck wrappers are built in two places, and passive because
       this only ever paints — it must never interfere with drag or tap
       handling.
       ---------------------------------------------------------------------- */
    const PRESS_TARGETS = '#player-deck-wrapper, #opponent-deck-wrapper, #player-portrait, #opponent-portrait';
    let pressed = null;

    function releasePress() {
        if (!pressed) return;
        pressed.classList.remove('mf-pressed');
        pressed = null;
    }

    function installPressFeedback() {
        if (document.body.dataset.mfPressFeedback === '1') return;
        document.body.dataset.mfPressFeedback = '1';
        const opts = { passive: true, capture: true };
        document.addEventListener('pointerdown', (event) => {
            const target = event.target?.closest?.(PRESS_TARGETS);
            releasePress();
            if (!target || !document.body.classList.contains('game-active')) return;
            pressed = target;
            target.classList.add('mf-pressed');
        }, opts);
        /* NOT `pointerleave`: it fires for every element the pointer exits and
           does not bubble, so a capturing document listener released the press
           on the very next event — the class was added and removed before a
           frame could paint it. Only a genuine end-of-press releases. */
        for (const type of ['pointerup', 'pointercancel']) {
            document.addEventListener(type, releasePress, opts);
        }
        window.addEventListener('blur', releasePress, { passive: true });
        /* A pointer dragged off the element mid-press must not leave it stuck. */
        document.addEventListener('pointermove', (event) => {
            if (pressed && event.target?.closest?.(PRESS_TARGETS) !== pressed) releasePress();
        }, opts);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', installPressFeedback, { once: true });
    } else {
        installPressFeedback();
    }

    // Call update function on module load to fix any existing decks
    setTimeout(updateExistingDeckSizes, 500);

    // Public API
    return {
        setupIndicators,
        clearStatusIndicators,
        // Pure segmentation of a status string into its individual effects.
        // Exposed because it is a contract in its own right — see
        // tests/unit/ui/statusEffectSeparators.test.js.
        statusEffects,
        setupPortraitDropTargets,
        preparePortraitBackground,
        enhancePortraitImage,
        debugDeckCardSizing,
        updateExistingDeckSizes
    };
})();

export { Indicators };
